import type { UserRoleId } from '@/types/authorisation/role-id.enum'
import type { RouterHook } from '@/types/router/router-hook.type'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user.store'
import { isArrayWithData } from '@/utils/array/is-array-with-data'
import { isRoleAllowed } from '@/utils/authorization/role-hierarchy'

export interface SettingsAuthorize {
  allowedMinimumRole?: UserRoleId
  allowedRolesExplicit?: UserRoleId[]
  disallowedRedirectTo?: string
  forms?: boolean
}

export const authorizeHook: RouterHook = {
  beforeEachAuthenticated: async (to, _, next) => {
    const authorizeObject = to.meta.authorize ?? {}

    const settings: Partial<SettingsAuthorize> = {
      allowedMinimumRole: undefined,
      allowedRolesExplicit: undefined,
      disallowedRedirectTo: 'app-dashboard',
      ...authorizeObject,
    }

    const routeNameTo = to.name?.toString()

    const { isFuelDataContributor, isPartnerTechnician, isSiteFuelDataContributor, isSysAdmin, roleId } = storeToRefs(useUserStore())

    if (isFuelDataContributor.value && routeNameTo && (
      routeNameTo.startsWith('admin-')
      || routeNameTo.startsWith('app-')
    )) {
      return next({ name: 'fuel-data-contributor-dashboard' })
    }

    if (isSiteFuelDataContributor.value && routeNameTo && (
      routeNameTo.startsWith('admin-')
      || routeNameTo.startsWith('app-')
    )) {
      return next({ name: 'summary-dashboard' })
    }

    if (
      isPartnerTechnician.value && routeNameTo && (
        routeNameTo.startsWith('admin-')
        || routeNameTo.startsWith('app-')
      )
    ) {
      return next({ name: 'technician-dashboard' })
    }

    if (
      isSysAdmin.value && routeNameTo && (
        !(routeNameTo.startsWith('admin-'))
        && !(routeNameTo.startsWith('app-settings-'))
        && !(routeNameTo.startsWith('app-support'))
      )
    ) {
      return next({ name: 'admin-dashboard' })
    }

    if (settings.allowedMinimumRole && !isRoleAllowed(roleId.value, settings.allowedMinimumRole)) {
      return next({ name: settings.disallowedRedirectTo })
    }

    if (settings.allowedRolesExplicit && isArrayWithData(settings.allowedRolesExplicit)) {
      if (!settings.allowedRolesExplicit.includes(roleId.value)) {
        return next({ name: settings.disallowedRedirectTo })
      }
    }
  },
}
